import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { GetSingleProductThunk } from "../../redux/states/products/thunk";
import {
  ArrowLeft,
  Package,
  Tag,
  Layers,
  Image as ImageIcon,
  BarChart3,
  ShoppingCart,
  Eye,
  EyeOff,
  Calendar,
  Hash,
  Weight,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
  IndianRupee,
  Percent,
  Shield,
  Briefcase,
  Gift,
} from "lucide-react";

const ViewProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleProduct, loading } = useSelector((state) => state.products);

  useEffect(() => {
    dispatch(GetSingleProductThunk(uuid));
  }, [dispatch, uuid]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading product details...
            </p>
            <p className="text-gray-500 mt-2">Please wait</p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singleProduct) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
          <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Product Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The product you are looking for is not available.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto transition-all duration-300 transform hover:scale-105"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Product List
            </button>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
        };
      case "inactive":
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
        };
    }
  };

  const StatusBadge = getStatusBadge(singleProduct.status);
  const StatusIcon = StatusBadge.icon;

  // Calculate profit margin
  const profitMargin = (
    parseFloat(singleProduct.mrp || 0) -
    parseFloat(singleProduct.wholesale_price || 0)
  ).toFixed(2);

  // Calculate discount percentage
  const discountPercentage = singleProduct.discount
    ? singleProduct.discount
    : singleProduct.mrp && singleProduct.selling_price
    ? (
        ((parseFloat(singleProduct.mrp) -
          parseFloat(singleProduct.selling_price)) /
          parseFloat(singleProduct.mrp)) *
        100
      ).toFixed(2)
    : 0;

  return (
    <NonAuthLayout>
      <div className="min-h-screen py-2 px-1 sm:px-2 lg:px-2">
        <div className="max-w-12xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 mb-2">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-2 lg:mb-0">
                <div className="flex items-center gap-3 mb-2">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="text-sm font-bold text-gray-800">
                    {singleProduct?.name || "Premium Regular Basmati Rice 25kg"}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold border flex items-center gap-1 ${StatusBadge.color}`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    {singleProduct?.status || "active"}
                  </span>
                </div>
                <p className="text-gray-600 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  SKU: {singleProduct?.sku || "PUL005"} | UUID:{" "}
                  {singleProduct?.uuid ||
                    "c242a336-0441-43e7-becd-dd24200a2834"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold border flex items-center gap-1 ${
                    singleProduct.visibility === 1 ||
                    singleProduct.visibility === undefined
                      ? "bg-blue-100 text-blue-800 border-blue-200"
                      : "bg-gray-100 text-gray-800 border-gray-200"
                  }`}
                >
                  {singleProduct.visibility === 1 ||
                  singleProduct.visibility === undefined ? (
                    <>
                      <Eye className="w-4 h-4" />
                      Visible
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4" />
                      Hidden
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
            {/* Left Column - Image and Stats */}
            <div className="lg:col-span-1 space-y-6">
              {/* Product Image */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-blue-600" />
                  Product Image
                </h2>
               
                {singleProduct.image ? (
                  <div className="relative">
                    <img
                      src={`${process.env.REACT_APP_API_URL}/${singleProduct.image}`}
                      alt={singleProduct.name}
                      className="w-full h-64 object-cover rounded-xl border-2 border-gray-200 shadow-sm"
                    />
                    <div className="absolute top-3 right-3 bg-black bg-opacity-50 text-white px-2 py-1 rounded-lg text-sm">
                      Featured
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-64 flex flex-col items-center justify-center bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 text-gray-400">
                    <ImageIcon className="w-16 h-16 mb-2" />
                    <p>No Image Available</p>
                  </div>
                )}

                {/* Gallery Product Image */}
                {singleProduct?.product_images_data?.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">
                      Gallery Images
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      {singleProduct.product_images_data.map((img, index) => (
                        <div key={index} className="relative">
                          <img
                            src={`${process.env.REACT_APP_API_URL}/${img.image_path}`}
                            alt={`${singleProduct.name}-${index}`}
                            className="w-full h-24 object-cover rounded-lg border border-gray-200 shadow-sm"
                          />
                          <div className="absolute bottom-1 right-1 bg-black bg-opacity-50 text-white px-1 py-0.5 rounded text-xs">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Quick Stats
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-green-700 font-medium">
                      Available Stock
                    </span>
                    <span className="text-2xl font-bold text-green-700">
                      {singleProduct.available_stock || "133.00"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-blue-700 font-medium">
                      Total Stock
                    </span>
                    <span className="text-2xl font-bold text-blue-700">
                      {singleProduct.stock_quantity || "133.000"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Coupon Information */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-600" />
                  Coupon Information
                </h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <span className="text-purple-700 font-medium">
                      Coupon Code
                    </span>
                    <span className="text-xl font-bold text-purple-700">
                      {singleProduct.coupon_code || "Get10"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-pink-50 rounded-lg border border-pink-200">
                    <span className="text-pink-700 font-medium">
                      Coupon Value
                    </span>
                    <span className="text-xl font-bold text-pink-700">
                      {singleProduct.coupon_val || "12"}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-600" />
                  Variants Information
                </h2>

                <div className="space-y-3">
                  {/* Variants */}
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-gray-700 font-medium block mb-2">
                      Available Variants
                    </span>

                    {singleProduct?.product_variants_data?.length > 0 ? (
                      Object.values(
                        singleProduct.product_variants_data.reduce(
                          (acc, item) => {
                            if (!acc[item.variant_name]) {
                              acc[item.variant_name] = [];
                            }
                            if (item.variant_value) {
                              acc[item.variant_name].push(item.variant_value);
                            }
                            return acc;
                          },
                          {}
                        )
                      ).map((values, index) => {
                        const variantName = Object.keys(
                          singleProduct.product_variants_data.reduce(
                            (acc, item) => {
                              acc[item.variant_name] = true;
                              return acc;
                            },
                            {}
                          )
                        )[index];

                        return (
                          <div key={index} className="mb-2">
                            <p className="text-sm font-semibold text-gray-800">
                              {variantName}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {values.map((val, i) => (
                                <span
                                  key={i}
                                  className="px-3 py-1 text-sm bg-white border rounded-full text-gray-700"
                                >
                                  {val}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500">
                        No variants available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Pricing Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-green-600" />
                  Pricing Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border rounded-xl p-4 bg-green-50 border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Selling Price
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-green-700">
                      ₹{singleProduct.wholesale_price || "231.00"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-blue-50 border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        MRP
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-blue-700">
                      ₹{singleProduct.mrp || "435.00"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-yellow-50 border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Percent className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Discount
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-yellow-700">
                      {discountPercentage}%
                    </p>
                  </div>

                  <div className="md:col-span-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          Profit Margin
                        </p>
                        <p className="text-lg font-bold text-green-600">
                          ₹{profitMargin}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Category Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-purple-600" />
                  Category Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Main Category
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.category_id_data || "Accessories"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ID: {singleProduct.category_id || "14"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Sub Category
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.sub_category_id_data || "Ear Rings"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ID: {singleProduct.sub_category_id || "26"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Special Category
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      ID: {singleProduct.special_category_id || "N/A"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Brand
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.brand_name || "Jackson Lewis"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ID: {singleProduct.brand_id || "1"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-orange-600" />
                  Product Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Weight className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Unit Type
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.unit_type || "pcs"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Unit ID: {singleProduct.unit_id || "1"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingCart className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Minimum Order Qty
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.min_order_qty || "1.00"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Hash className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Product ID
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.id || "29"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Created On
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.created_at
                        ? new Date(singleProduct.created_at).toLocaleDateString(
                            "en-IN"
                          )
                        : "2025-12-16"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-medium text-gray-700">
                        Last Updated
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.updated_at
                        ? new Date(singleProduct.updated_at).toLocaleDateString(
                            "en-IN"
                          )
                        : "2025-12-16"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Descriptions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-blue-600" />
                    Short Description
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 min-h-32">
                    <p className="text-gray-700 leading-relaxed">
                      {singleProduct.short_description || "test"}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-blue-600" />
                    Long Description
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 min-h-32">
                    <p className="text-gray-700 leading-relaxed">
                      {singleProduct.long_description || "test"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Hash className="w-5 h-5 text-gray-600" />
                  Additional Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Slug
                    </p>
                    <p className="text-lg font-semibold text-gray-900 truncate">
                      {singleProduct.slug ||
                        "premium-regular-basmati-rice-25kg"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Unit Data
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.unit_id_data || "Piece"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Visibility
                    </p>
                    <p
                      className={`text-lg font-semibold ${
                        singleProduct.visibility === 1 ||
                        singleProduct.visibility === undefined
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {singleProduct.visibility === 1 ||
                      singleProduct.visibility === undefined
                        ? "Visible"
                        : "Hidden"}
                    </p>
                  </div>

                  <div className="border rounded-xl p-4 bg-white hover:shadow-md transition-shadow duration-300">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Gallery Images Count
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {singleProduct.product_images_data?.length || 8}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

export default ViewProducts;
